<script lang="ts" setup>
import { computed, inject, onMounted } from 'vue'
import { useChapterTheme } from '../composables/useChapterTheme'
import {
  NOVELMELLO_READER_BACKGROUNDS,
  NOVELMELLO_READER_DEFAULT_BACKGROUND,
  NOVELMELLO_READER_TEXTS,
} from '@/pages/chapter/configs/novelmello'
import type { ChapterConfig } from '@/composables/payment'

interface Props {
  value: boolean
  chapter: any
}

defineProps<Props>()
const emit = defineEmits<{
  (e: 'input', value: boolean): void
  (e: 'prev-chapter'): void
  (e: 'next-chapter'): void
  (e: 'open-toc'): void
}>()

const config = inject<ChapterConfig>('chapterConfig')!
const { theme, THEME_FONT_MAP, onChangeThemeSize } = useChapterTheme()

const backgrounds = NOVELMELLO_READER_BACKGROUNDS as unknown as string[]
const colorLabel = NOVELMELLO_READER_TEXTS.colorLabel

/** 展示用字号:存储档位是 2 倍值(24~50),正文实际渲染 px = 档位 / 2。仅展示映射,不改存储 */
const displaySize = computed(() => Math.round(Number(theme.value.size) / 2))
const isMinSize = computed(() => theme.value.size === THEME_FONT_MAP[0])
const isMaxSize = computed(() => theme.value.size === THEME_FONT_MAP[THEME_FONT_MAP.length - 1])
const activeBackground = computed(() =>
  backgrounds.includes(theme.value.background) ? theme.value.background : NOVELMELLO_READER_DEFAULT_BACKGROUND,
)

onMounted(() => {
  // 仅纠正"不属于 novelmello 色板"的历史值,保证页面底色与皮肤类一致;合法值不动
  if (!backgrounds.includes(theme.value.background))
    theme.value.background = NOVELMELLO_READER_DEFAULT_BACKGROUND
})

/** novelmello 不使用图片皮肤(THEME_SKINS),切换颜色时清空 skinId,保持 composable 状态自洽 */
function selectBackground(color: string) {
  theme.value.background = color
  theme.value.skinId = ''
}

function handlePrev() {
  emit('prev-chapter')
}

function handleNext() {
  emit('next-chapter')
}

function handleOpenTOC() {
  emit('open-toc')
}
</script>

<template>
  <BasicPopup :value="value" :mask="false" @input="emit('input', $event)">
    <div class="nm-reader-panel">
      <div class="nm-row">
        <p class="nm-label">
          {{ config.texts.themeSizeLabel }}
        </p>
        <div class="nm-size-group">
          <span class="nm-size-btn" :class="{ 'is-disabled': isMinSize }" @click="onChangeThemeSize(-1)">
            {{ config.texts.fontSizeDecrease }}
          </span>
          <span class="nm-size-value">{{ displaySize }}</span>
          <span class="nm-size-btn" :class="{ 'is-disabled': isMaxSize }" @click="onChangeThemeSize(1)">
            {{ config.texts.fontSizeIncrease }}
          </span>
        </div>
      </div>

      <div class="nm-row nm-row--color">
        <p class="nm-label">
          {{ colorLabel }}
        </p>
        <div class="nm-color-group">
          <span
            v-for="item in backgrounds"
            :key="item"
            class="nm-color-dot"
            :class="{ 'is-active': item === activeBackground }"
            :style="{ background: item }"
            @click="selectBackground(item)"
          />
        </div>
      </div>

      <div class="nm-actions">
        <span class="nm-action nm-action--soft" :class="{ 'is-disabled': !chapter?.prevId }" @click="handlePrev">
          {{ config.texts.prevButton }}
        </span>
        <span class="nm-action nm-action--pink" @click.stop="handleOpenTOC">
          {{ config.texts.catalogueButton }}
        </span>
        <span class="nm-action nm-action--soft" :class="{ 'is-disabled': !chapter?.nextId }" @click="handleNext">
          {{ config.texts.nextButton }}
        </span>
      </div>
    </div>
  </BasicPopup>
</template>

<style lang="scss" scoped>
/* 与 src/pages/chapter/components/ChapterThemeSettings.novelmello.vue 的 scoped 样式完全一致 */
.nm-reader-panel {
  width: 100%;
  box-sizing: border-box;
  padding: 20px 20px calc(20px + env(safe-area-inset-bottom));
  background: #FFFFFF;
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -8px 28px rgba(124, 77, 232, 0.12);
}

.nm-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.nm-row--color {
  margin-top: 18px;
}

.nm-label {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #2B1B34;
  white-space: nowrap;
}

.nm-size-group {
  display: flex;
  align-items: center;
}

.nm-size-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 20px;
  border-radius: 17px;
  background: #F3EDFE;
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  color: #2B1B34;
  cursor: pointer;
  user-select: none;

  &.is-disabled {
    opacity: 0.4;
  }
}

.nm-size-value {
  min-width: 44px;
  text-align: center;
  font-size: 15px;
  font-weight: 800;
  color: #7C4DE8;
}

.nm-color-group {
  display: flex;
  align-items: center;
}

.nm-color-dot {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 1px solid #E5DCE8;
  cursor: pointer;
  transition: border-color 0.18s ease, transform 0.18s ease;

  & + & {
    margin-left: 12px;
  }

  &.is-active {
    border: 2px solid #F0509B;
    transform: scale(1.06);
  }
}

.nm-actions {
  display: flex;
  align-items: center;
  margin-top: 20px;
}

.nm-action {
  flex: 1;
  height: 42px;
  border-radius: 21px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  user-select: none;

  /* 不使用 flex gap(项目 scan:flex-gap 兼容性约束) */
  & + & {
    margin-left: 10px;
  }

  &.is-disabled {
    opacity: 0.45;
  }
}

.nm-action--soft {
  background: #F3EDFE;
  color: #71607E;
}

.nm-action--pink {
  background: #FDEFF6;
  color: #F0509B;
}
</style>
